// =================================================================================================
// >>> **NO PDF DOWNLOADS OF READINGS (her ruling, 2026-10-17).** <<<
//
// Her words: *"I'm going to take away the ability to download a PDF reading, all birth charts, readings,
// and generations have to be opened on the site only."* And the exception, also hers: *"they can
// obviously download seals they create, petitions... And a spell from the cauldron... the magic room is
// fine."* The Spellbook counts as the Magic Room (her word, same day).
//
// ONE question, asked by every Download button AND by the server's own PDF door, so a button that is
// gone and a door that still answers can never disagree: the old app builds already on phones post to
// `/api/report/pdf` directly, and only the door can refuse them.
//
// What may still download is the Magic Room family the bytes lock already knows (`isMagicRoomDownload`:
// spells, the Cauldron, petitions, oils, herbs, keys, Moses, incantations, seals) plus every type the
// house files in the Magic Room or the seals room (`ROOM_FOR_TYPE`: chants, affirmations, notes). Nothing saved is deleted: every
// reading stays on its shelf and in My Room, to open any time.
// =================================================================================================
import { isMagicRoomDownload } from "../server/download-gating.js";
import { roomForType } from "./rooms.js";

// The rooms whose work may still leave as a PDF: the Magic Room (her "the magic room is fine") and the
// seals she creates. Read off the house's ONE type-to-room map, so a chant, an affirmation or a note
// saved in the Magic Room keeps its download without a second list that could drift from the shelves.
export const PDF_ROOMS = ["magic", "seals"];

/** May this package leave the house as a PDF? Only the Magic Room family, seals included. */
export function pdfAllowed(report) {
  if (!report || typeof report !== "object") return false;
  return isMagicRoomDownload(report) || PDF_ROOMS.includes(roomForType(report.type || report.kind));
}

// HER RULING, 2026-10-17 (second word): a spell, petition, chant, affirmation or other working Lala writes
// for a member in the Ask Lala chat may still go in a PDF, same as Magic Room work. Readings stay PDF-free.
// So a passage carries the KIND Lala says it is, and the door asks two questions: is the kind a working,
// and does the body read like a reading anyway (her placements, her numbers, her chart, her cards)?
export const WORKING_KINDS = ["spell", "petition", "chant", "affirmation", "incantation", "ritual", "working"];

const READING_SHAPES = [
  /\byour (?:natal |birth )?(?:sun|moon|rising|ascendant|mercury|venus|mars|jupiter|saturn|uranus|neptune|pluto|chiron|north node|south node|midheaven)\b(?: sign)? (?:is|sits|falls|in|at)\b/i,
  /\byour (?:birth |natal )?chart\b/i,
  /\blife path(?: number)?\b/i,
  /\b(?:expression|soul urge|destiny|personal year) number\b/i,
  /\byour (?:human design )?(?:type|profile|authority) is\b/i,
  /\b(?:the cards? (?:you|we) (?:pulled|drew)|your spread|in this spread)\b/i,
];

// >>> **IT #428 (2026-10-26): A WORKING WRITTEN BEFORE 10-17 CARRIES NO KIND, AND THE DOOR READ THAT AS A READING.** <<< The kind
// rides on `passage.working`, a field that exists only on documents made after her 10-17 ruling. Measured: 5,076 of 6,051 Ask Lala
// documents (335 members) have none, and every one was refused with "your readings live in your room", petitions and jar
// workings included. For those OLDER documents only, the kind is read off the title she was given, in the ruling's own words;
// a title that names no working stays refused, and `readingShaped` still guards the body exactly as it does for a new one.
const LEGACY_KIND_WORDS = [
  ["petition", /\b(?:petitions?|prayers?)\b|\bpetici(?:o|ó)n(?:es)?(?![a-z])/i],
  ["spell", /\bspells?\b/i],
  ["chant", /\bchants?\b/i],
  // D11 (2026-10-27): invocations, ceremonies, intentions and protection workings were missed by the first #428 list (88
  // workings, 39 members, still refusing to open). A launch plan or a voice guide is still not a working.
  ["affirmation", /\b(?:affirmations?|declarations?|decrees?|intentions?)\b/i],
  ["incantation", /\b(?:incantations?|invocations?)\b/i],
  ["ritual", /\b(?:rituals?|rites?|ceremon(?:y|ies)|protections?|baths?|jars?|altars?|cleansings?|cleanses?|clearings?|wards?|candles?|oils?|activations?|removals?|releases?|blessings?|wash(?:es)?)\b/i],
  ["working", /\bworkings?\b/i],
];
export function legacyPassageKind(title) {
  const t = String(title || "");
  for (const [kind, re] of LEGACY_KIND_WORDS) if (re.test(t)) return kind;
  return "";
}

/** Does this text read like a reading (her placements, numbers, chart, design or cards)? */
export function readingShaped(body) {
  const text = String(body || "");
  return READING_SHAPES.some((re) => re.test(text));
}

/** May something Lala wrote in the conversation leave as a PDF? Only a working, never a reading. */
export function passageAllowed(passage) {
  if (!passage || typeof passage !== "object") return false;
  const kind = String(passage.kind || "").trim().toLowerCase();
  return WORKING_KINDS.includes(kind) && Boolean(String(passage.body || "").trim()) && !readingShaped(passage.body);
}

/** What Lala is told when what she was asked to put in a PDF is a reading, not a working. */
export const PASSAGE_READING_RESULT =
  "NOT MADE: that is a reading, and readings are never put in a PDF (the owner's ruling). NOTHING was made. "
  + "Tell her warmly, in one line, that readings stay in the conversation and in her room to open any time. "
  + "Spells, petitions, chants, affirmations and workings you write for her can go in a PDF. Never say a file is coming.";

/** What Lala is told when everything a member asked to have compiled is a reading. Her voice, our facts. */
export const READINGS_STAY_RESULT =
  "NOT MADE: everything she asked for is a reading, chart or report, and those are never put in a PDF (the owner's ruling). "
  + "NOTHING was compiled. Tell her warmly, in one or two lines, that her readings live in her room to open any time on the site "
  + "or in the app (Shelves, then the room it was made in). Do not read them out in the chat instead, and never say a file is coming.";

/** The line added when some of what she named was readings and they were left out of the document. */
export const READINGS_LEFT_NOTE =
  "ALSO SAY, warmly and in one line: her readings were not put in the document, because readings stay in her room to open any time on the site or in the app.";

/** What the door says to an old app build (or anyone) asking for a reading PDF. No em dashes. */
export const READING_PDF_REFUSAL =
  "Your readings live in your room now. Open Shelves, pick the room, and it is there to read any time, on the site or in the app.";
